import { Building2, ChevronRight, CreditCard, Pencil, SearchX, Users } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OrganizationTreeItem } from "../types/clients-api.types";
import { CountryLabel } from "./CountryLabel";
import { TablePaginationFooter } from "./TablePaginationFooter";

export interface OrganizationsTableProps {
  rows: OrganizationTreeItem[];
  isLoading: boolean;
  /** When the empty page comes from applied filters, the empty state offers "clear filters" instead of a CTA. */
  hasActiveFilters: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onOpenOrganization: (organization: OrganizationTreeItem) => void;
  onEditOrganization?: (organizationId: string) => void;
  onViewAccounts?: (organization: OrganizationTreeItem) => void;
  onClearFilters: () => void;
  onCreateOrganization?: () => void;
}

/** Level 1 of the master-detail: one row per organization with derived counters; rows drill down. */
export function OrganizationsTable({
  rows,
  isLoading,
  hasActiveFilters,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onOpenOrganization,
  onEditOrganization,
  onViewAccounts,
  onClearFilters,
  onCreateOrganization,
}: OrganizationsTableProps) {
  const { t } = useTranslations("clients");

  const columns = useMemo<ColocaDataTableColumn<OrganizationTreeItem>[]>(
    () => [
      {
        id: "organization",
        header: t("orgs_th_organization"),
        cell: (row) => (
          <div className="flex items-center gap-3">
            <ColocaAvatar name={row.name} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{row.name}</p>
              <p className="truncate text-xs text-muted-foreground" title={row.email}>
                {row.email}
              </p>
            </div>
          </div>
        ),
        cellClassName: "max-w-70",
      },
      {
        id: "country",
        header: t("filters_label_country"),
        cell: (row) => <CountryLabel code={row.country} />,
      },
      {
        id: "clients",
        header: t("orgs_th_clients"),
        cell: (row) => (
          <span className="text-sm tabular-nums">
            <span
              className={row.clients.length === 0 ? "text-muted-foreground" : "font-medium text-foreground"}
            >
              {row.clients.length.toLocaleString()}
            </span>{" "}
            <span className="text-xs text-muted-foreground">{t("orgs_count_clients")}</span>
          </span>
        ),
      },
      {
        id: "accounts",
        header: t("orgs_th_accounts"),
        cell: (row) => (
          <span className="text-sm tabular-nums">
            <span
              className={
                row.accountIds.length === 0 ? "text-muted-foreground" : "font-medium text-foreground"
              }
            >
              {row.accountIds.length.toLocaleString()}
            </span>{" "}
            <span className="text-xs text-muted-foreground">{t("orgs_count_accounts")}</span>
          </span>
        ),
      },
      {
        id: "created",
        header: t("orgs_th_created"),
        cell: (row) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">{t("table_th_actions")}</span>,
        cell: (row) => (
          <div className="flex items-center justify-end gap-1 opacity-60 transition-opacity group-hover/row:opacity-100">
            <ColocaIconButton
              variant="ghost"
              size="sm"
              icon={Users}
              aria-label={t("orgs_action_view_clients")}
              title={t("orgs_action_view_clients")}
              onClick={() => onOpenOrganization(row)}
            />
            {onEditOrganization && (
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={Pencil}
                aria-label={t("action_edit")}
                title={t("action_edit")}
                onClick={() => onEditOrganization(row.id)}
              />
            )}
            {onViewAccounts && (
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={CreditCard}
                aria-label={t("action_accounts_aria")}
                title={t("action_accounts_aria")}
                onClick={() => onViewAccounts(row)}
              />
            )}
            <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
          </div>
        ),
        headerClassName: "text-right",
      },
    ],
    [t, onOpenOrganization, onEditOrganization, onViewAccounts],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={isLoading}
      skeletonRows={Math.min(pageSize, 8)}
      minWidth={860}
      onRowClick={onOpenOrganization}
      empty={
        hasActiveFilters ? (
          <ColocaEmpty
            icon={SearchX}
            title={t("empty_no_results_title")}
            description={t("empty_no_results_description")}
            surface="card"
          >
            <ColocaButton variant="default" onClick={onClearFilters}>
              {t("filters_button_clear")}
            </ColocaButton>
          </ColocaEmpty>
        ) : (
          <ColocaEmpty
            icon={Building2}
            title={t("empty_organizations_title")}
            description={t("empty_organizations_description")}
            surface="card"
          >
            {onCreateOrganization && (
              <ColocaButton variant="primary" onClick={onCreateOrganization}>
                {t("button_create_organization")}
              </ColocaButton>
            )}
          </ColocaEmpty>
        )
      }
      footer={
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          summaryKey="orgs_pagination_summary"
        />
      }
    />
  );
}
